import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { DataGrid, type GridColDef, type GridRenderCellParams, type GridSortModel } from "@mui/x-data-grid";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useDepartmentFlags } from "../../../hooks/useDepartmentFlags";
import { useExpandedGroups } from "../../../hooks/useExpandedGroups";
import type { ManagementCenterHPPDResponse, ManagementCenterPPDResponse } from "../../../utils/models";
import { formatCurrency } from "../../../utils/utilFunctions";
import { buildAugmentedRows, MC_GRID_SX, type MCTableAugmentedRow, sortRowsByModel } from "../utils";
import { ScheduleVsActualExpandedRow } from "./ScheduleVsActualExpandedRow";
import type { DayRow, DifferenceMode, Metric } from "./types";
import { buildDayRows, getDifferenceColor } from "./utils";

type Props = {
	data?: ManagementCenterPPDResponse | ManagementCenterHPPDResponse;
	metric: Metric;
	isLoading: boolean;
	roles?: string[];
	jobs?: string[];
	laborCost?: boolean;
	shiftHours?: string[];
	differenceMode: DifferenceMode;
	onDifferenceModeChange: (mode: DifferenceMode) => void;
	includeLateScheduled?: boolean;
	estimateMissingWage: boolean;
};

type AugmentedRow = MCTableAugmentedRow<DayRow>;

const TOTAL_COLUMNS = 7;
const EMPTY_SVA_ROW: Omit<DayRow, "date"> = { dateDisplay: "", census: 0, scheduled: 0, actual: 0, target: 0, difference: 0 };

const makeDifferenceComparator = (mode: DifferenceMode) => (a: DayRow, b: DayRow) => {
	const aBase = mode === "target" ? a.target : a.scheduled;
	const bBase = mode === "target" ? b.target : b.scheduled;
	const aVal = aBase !== 0 ? Math.abs(a.actual - aBase) / aBase : 0;
	const bVal = bBase !== 0 ? Math.abs(b.actual - bBase) / bBase : 0;
	return aVal - bVal;
};

export const ScheduleVsActualTable: FC<Props> = ({
	data,
	metric,
	isLoading,
	roles,
	jobs,
	shiftHours,
	laborCost,
	differenceMode,
	onDifferenceModeChange,
	includeLateScheduled,
	estimateMissingWage,
}) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { timezone } = useDepartmentFlags();
	const { expandedGroups, toggleGroupExpand } = useExpandedGroups();
	const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "dateDisplay", sort: "desc" }]);

	const isPpd = metric === "ppd";

	const rows = useMemo(() => buildDayRows(data, metric, timezone), [data, metric, timezone]);
	const diffComparator = useMemo(() => makeDifferenceComparator(differenceMode), [differenceMode]);
	const sortedRows = useMemo(() => sortRowsByModel(rows, sortModel, { difference: diffComparator }), [rows, sortModel, diffComparator]);
	const augmentedRows = useMemo(() => buildAugmentedRows(sortedRows, expandedGroups, EMPTY_SVA_ROW), [sortedRows, expandedGroups]);

	const handleSortModelChange = useCallback(
		(model: GridSortModel) => {
			setSortModel(model);
			if (model.length > 0) {
				track("schedule_vs_actual_sort_changed", { column: model[0].field, direction: model[0].sort });
			}
		},
		[track],
	);

	const handleRowClick = useCallback(
		(params: { id: string | number }) => {
			const id = params.id as string;
			if (id.endsWith("-detail")) return;
			const isExpanding = !expandedGroups.has(id);
			toggleGroupExpand(id);
			if (isExpanding) {
				track("schedule_vs_actual_row_expanded", { date: id, metric });
			}
		},
		[expandedGroups, toggleGroupExpand, track, metric],
	);

	const columns: GridColDef<AugmentedRow>[] = useMemo(
		() => [
			{
				field: "expand",
				headerName: "",
				width: 36,
				sortable: false,
				disableColumnMenu: true,
				resizable: false,
				display: "flex",
				colSpan: (_value: unknown, row: AugmentedRow) => (row._isDetail ? TOTAL_COLUMNS : 1),
				renderCell: (params: GridRenderCellParams<AugmentedRow>) => {
					if (params.row._isDetail) {
						return (
							<ScheduleVsActualExpandedRow
								date={params.row._parentDate}
								metric={metric}
								roles={roles}
								jobs={jobs}
								laborCost={laborCost}
								shiftHours={shiftHours}
								includeLateScheduled={includeLateScheduled}
								estimateMissingWage={estimateMissingWage}
							/>
						);
					}
					const isExpanded = expandedGroups.has(params.row.date);
					return (
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								handleRowClick({ id: params.row.date });
							}}
							aria-label={isExpanded ? t("schedule.vs.actual.collapse") : t("schedule.vs.actual.expand")}
							aria-expanded={isExpanded}
							tabIndex={-1}
						>
							{isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
						</IconButton>
					);
				},
			},
			{
				field: "dateDisplay",
				headerName: t("schedule.vs.actual.date"),
				flex: 1.4,
				minWidth: 140,
			},
			{
				field: "census",
				headerName: t("schedule.vs.actual.census"),
				flex: 0.8,
				minWidth: 80,
				align: "center",
				headerAlign: "center",
				type: "number",
			},
			{
				field: "scheduled",
				headerName: t("schedule.vs.actual.scheduled"),
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: (value: number) => (isPpd ? formatCurrency(value) : value.toFixed(2)),
			},
			{
				field: "actual",
				headerName: t("schedule.vs.actual.actual"),
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: (value: number) => (isPpd ? formatCurrency(value) : value.toFixed(2)),
			},
			{
				field: "target",
				headerName: t("schedule.vs.actual.target"),
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				valueFormatter: (value: number) => (isPpd ? formatCurrency(value) : value.toFixed(2)),
			},
			{
				field: "difference",
				renderHeader: () => {
					const altMode: DifferenceMode = differenceMode === "target" ? "schedule" : "target";
					return (
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								lineHeight: 1.2,
							}}
						>
							<Typography
								variant="caption"
								sx={{
									fontWeight: 600,
									lineHeight: 1.2,
								}}
							>
								{t("schedule.vs.actual.difference")}
							</Typography>
							<Typography
								variant="caption"
								component="span"
								sx={{ cursor: "pointer", color: "primary.main", fontSize: "0.65rem", lineHeight: 1.2, "&:hover": { textDecoration: "underline" } }}
								onClick={(e) => {
									e.stopPropagation();
									onDifferenceModeChange(altMode);
								}}
							>
								{differenceMode === "target" ? t("schedule.vs.actual.diff.vs.target") : t("schedule.vs.actual.diff.vs.schedule")}
							</Typography>
						</Box>
					);
				},
				flex: 1,
				minWidth: 100,
				align: "center",
				headerAlign: "center",
				type: "number",
				display: "flex",
				renderCell: (params: GridRenderCellParams<AugmentedRow>) => {
					const row = params.row;
					const base = differenceMode === "target" ? row.target : row.scheduled;
					const diff = row.actual - base;
					const color = getDifferenceColor(base, row.actual);
					return (
						<Typography
							variant="body2"
							sx={{
								color,
								fontWeight: diff !== 0 ? 600 : 400,
								lineHeight: "inherit",
							}}
						>
							{isPpd ? formatCurrency(diff) : diff.toFixed(2)}
						</Typography>
					);
				},
			},
		],
		[
			t,
			isPpd,
			expandedGroups,
			metric,
			handleRowClick,
			roles,
			jobs,
			shiftHours,
			laborCost,
			differenceMode,
			onDifferenceModeChange,
			includeLateScheduled,
			estimateMissingWage,
		],
	);

	if (isLoading) {
		return (
			<CommonDashboardBox sx={{ flex: 1, minWidth: 0 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						py: 4,
					}}
				>
					<CircularProgress size={24} />
				</Box>
			</CommonDashboardBox>
		);
	}

	if (rows.length === 0) {
		return (
			<CommonDashboardBox sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						textAlign: "center",
						py: 4,
					}}
				>
					{t("schedule.vs.actual.no.data")}
				</Typography>
			</CommonDashboardBox>
		);
	}

	return (
		<CommonDashboardBox sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
			<DataGrid
				rows={augmentedRows}
				columns={columns}
				getRowId={(row) => row.date}
				sortingMode="server"
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				onRowClick={handleRowClick}
				getRowHeight={({ id }) => ((id as string).endsWith("-detail") ? "auto" : undefined)}
				density="compact"
				disableColumnMenu
				disableColumnFilter
				hideFooter
				rowSelection={false}
				sx={{
					...MC_GRID_SX,
					maxHeight: "60vh",
					"& .MuiDataGrid-row": {
						cursor: "pointer",
					},
				}}
			/>
		</CommonDashboardBox>
	);
};
